import { h } from 'vue'
import { defineStore } from 'pinia'
import type { RouteRecordRaw } from 'vue-router'
import type { ErrorMessageMode } from '@/types/axios'
import * as authUtil from '@/utils/auth'
import { store } from '@/store'
import { router } from '@/router'
import type { RoleEnum } from '@/enums/roleEnum'
import { PageEnum } from '@/enums/pageEnum'
import { ACCESS_TOKEN_KEY, COMPANY_INFO_KEY, REFRESH_TOKEN_KEY, ROLES_KEY, USER_INFO_KEY } from '@/enums/cacheEnum'
import { PAGE_NOT_FOUND_ROUTE } from '@/router/routes/basic'
import { usePermissionStore } from '@/store/modules/permission'
import { useI18n } from '@/hooks/web/useI18n'
import { useMessage } from '@/hooks/web/useMessage'
import { getAuthCache, setAuthCache } from '@/utils/auth'
import { getCompanyAsTenant } from '@/api/base/login'
import { doLogout, getUserInfo, loginApi, smsLogin } from '@/api/base/user'
import type { GetUserInfoModel, LoginParams, SmsLoginParams, userModel } from '@/api/base/model/userModel'

import { isArray } from '@/utils/is'

interface UserState {
  userInfo: Nullable<GetUserInfoModel>
  accessToken?: string
  refreshToken?: string
  roleList: RoleEnum[]
  sessionTimeout?: boolean
  lastUpdateTime: number
  user: userModel | null | undefined
  companyInfo: Recordable
  userAcconut: Recordable
}

export const useUserStore = defineStore('app-user', {
  state: (): UserState => ({
    user: null,
    // user info
    userInfo: null,
    // token
    accessToken: undefined,
    refreshToken: undefined,
    // roleList
    roleList: [],
    // Whether the login expired
    sessionTimeout: false,
    // Last fetch time
    lastUpdateTime: 0,
    userAcconut: {},
    companyInfo: {},
  }),
  getters: {
    getUserInfo(state): GetUserInfoModel {
      return state.userInfo || getAuthCache<GetUserInfoModel>(USER_INFO_KEY) || {}
    },
    getAccessToken(state): string {
      return state.accessToken || getAuthCache<string>(ACCESS_TOKEN_KEY)
    },
    getRefreshToken(state): string {
      return state.refreshToken || getAuthCache<string>(REFRESH_TOKEN_KEY)
    },
    getRoleList(state): RoleEnum[] {
      return state.roleList.length > 0 ? state.roleList : getAuthCache<RoleEnum[]>(ROLES_KEY)
    },
    getSessionTimeout(state): boolean {
      return !!state.sessionTimeout
    },
    getLastUpdateTime(state): number {
      return state.lastUpdateTime
    },
    //  获取登录的账号密码
    getUserAccount(state): Recordable {
      return state.userAcconut
    },
    /* 获取公司资料以及备案号 */
    getCompanyInfo(state): Recordable {
      return state.companyInfo
    },
  },
  actions: {
    setAccessToken(info: string | undefined) {
      this.accessToken = info || '' // for null or undefined value
      setAuthCache(ACCESS_TOKEN_KEY, info)
    },
    setRefreshToken(info: string | undefined) {
      this.refreshToken = info || '' // for null or undefined value
      setAuthCache(REFRESH_TOKEN_KEY, info)
    },
    setRoleList(roleList: RoleEnum[]) {
      this.roleList = roleList
      setAuthCache(ROLES_KEY, roleList)
    },
    setUserInfo(info: GetUserInfoModel | null) {
      this.userInfo = info
      this.user = info?.user // 更新个人中心信息
      // console.log('setUserInfo123', getUserInfo)
      this.lastUpdateTime = new Date().getTime()
      setAuthCache(USER_INFO_KEY, info)
    },
    setSessionTimeout(flag: boolean) {
      this.sessionTimeout = flag
    },
    setUserAccount(info: Recordable) {
      this.userAcconut = info
    },
    setCompanyInfo(info: Recordable) {
      this.companyInfo = info
      localStorage.setItem(COMPANY_INFO_KEY, JSON.stringify(info))
    },
    resetState() {
      this.userInfo = null
      this.accessToken = ''
      this.companyInfo = {}
      this.roleList = []
      this.sessionTimeout = false
    },
    /**
     * @description: login
     */
    async login(
      params: LoginParams & {
        goHome?: boolean
        mode?: ErrorMessageMode
      },
    ): Promise<GetUserInfoModel | null | boolean> {
      try {
        const { goHome = true, mode, ...loginParams } = params
        const data = await loginApi(loginParams, mode)
        const { accessToken, refreshToken, isUpdatePassword } = data
        // save token
        this.setAccessToken(accessToken)
        this.setRefreshToken(refreshToken)
        if (isUpdatePassword === false)
          return isUpdatePassword
        else
          return this.afterLoginAction(goHome)
      }
      catch (error) {
        return Promise.reject(error)
      }
    },
    async smsLogin(
      params: SmsLoginParams & {
        goHome?: boolean
        mode?: ErrorMessageMode
      },
    ): Promise<GetUserInfoModel | null> {
      try {
        const { goHome = true, mode, ...smsLoginParams } = params
        const data = await smsLogin(smsLoginParams, mode)
        const { accessToken, refreshToken } = data
        // save token
        this.setAccessToken(accessToken)
        this.setRefreshToken(refreshToken)
        return this.afterLoginAction(goHome)
      }
      catch (error) {
        return Promise.reject(error)
      }
    },
    async afterLoginAction(goHome?: boolean): Promise<GetUserInfoModel | null> {
      if (!this.getAccessToken)
        return null
        // get user info
      const userInfo = await this.getUserInfoAction()
      const sessionTimeout = this.sessionTimeout
      if (sessionTimeout) {
        this.setSessionTimeout(false)
      }
      else {
        const permissionStore = usePermissionStore()
        if (!permissionStore.isDynamicAddedRoute) {
          const routes = await permissionStore.buildRoutesAction()
          routes.forEach((route) => {
            try {
              router.addRoute(route as unknown as RouteRecordRaw)
            }
            catch (e) {}
          })
          router.addRoute(PAGE_NOT_FOUND_ROUTE as unknown as RouteRecordRaw)
          permissionStore.setDynamicAddedRoute(true)
        }
        goHome && (await router.replace(PageEnum.BASE_HOME))
      }
      return userInfo
    },
    async getUserInfoAction(): Promise<GetUserInfoModel | null> {
      if (!this.getAccessToken)
        return null

      // @xudanya
      /* 判断是不是开票系统 */
      let userInfo = {} as GetUserInfoModel

      if (window.location.origin.includes('kpxt'))
        userInfo = await getUserInfo({ menuMode: 2 })
      else
        userInfo = await getUserInfo()

      // if (window.location.origin.includes('kpxt'))
      // userInfo = await getUserInfo({ menuMode: 2 })
      // else
      //   userInfo = await getUserInfo()
      const { roles = [] } = userInfo
      if (isArray(roles)) {
        const roleList = roles.map(item => item) as RoleEnum[]
        this.setRoleList(roleList)
      }
      else {
        userInfo.roles = []
        this.setRoleList([])
      }
      this.setUserInfo(userInfo)
      return userInfo
    },
    /**
     * @description: logout
     */
    async logout(goLogin = false) {
      if (this.getAccessToken) {
        try {
          await doLogout()
        }
        catch {
          console.log('注销Token失败')
        }
      }
      this.setAccessToken(undefined)
      this.setSessionTimeout(false)
      this.setUserInfo(null)
      goLogin && router.push(PageEnum.BASE_LOGIN)
    },

    /**
     * @description: Confirm before logging out
     */
    confirmLoginOut() {
      const { createConfirm } = useMessage()
      const { t } = useI18n()
      createConfirm({
        iconType: 'warning',
        title: () => h('span', t('sys.app.logoutTip')),
        content: () => h('span', t('sys.app.logoutMessage')),
        onOk: async () => {
          await this.logout(true)
        },
      })
    },

    /**
     * 查询公司资料详情
     */
    async getCompanyInfoApi() {
      // const data = await this.getCompanyInfo

      const tenant = authUtil.getTenant()
      const res = await getCompanyAsTenant(tenant.id)
      this.setCompanyInfo(res)
    },

  },
  persist: {
    key: 'user',
    // 部分持久化状态的点符号路径数组，[]意味着没有状态被持久化(默认为undefined，持久化整个状态)
    // paths: ['userInfo','companyInfo']
  },
})

// Need to be used outside the setup
export function useUserStoreWithOut() {
  return useUserStore(store)
}
